import React from 'react';
import moment from 'moment';

import Dialog, { DialogProps } from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';

import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';

import { getContractBonusBreakdown } from 'api/players';
import { ContractBonusBreakdown, PlayerBonusContract } from 'types/backend/PlayerBonusContract';
import {
    bonusAmountsMismatch,
    formatAbi,
    formatFairRoi,
    formatFractionAsPercent,
    formatPercent,
    formatTsMonth,
    formatUsd,
    formatYesNo,
    payoutShareFraction,
} from './bonusFormatters';

interface BonusBreakdownDialogProps extends DialogProps {
    contractSummary: PlayerBonusContract | null;
}

const formatContractPeriod = (summary: PlayerBonusContract): string => {
    const start = moment(summary.start_datetime).format('DD MMM YYYY HH:mm');
    if (summary.is_current || !summary.end_datetime) {
        return `${start} — текущий`;
    }
    const end = moment(summary.end_datetime).format('DD MMM YYYY HH:mm');
    return `${start} — ${end}`;
};

const SummaryStats: React.FC<{ summary: PlayerBonusContract }> = ({ summary }) => (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 text-sm">
        <div>
            <span className="text-gray-500">ABI: </span>
            {formatAbi(summary.avg_abi)}
        </div>
        <div>
            <span className="text-gray-500">Т/мес: </span>
            {formatTsMonth(summary.avg_ts_month)}
        </div>
        <div>
            <span className="text-gray-500">Fair ROI: </span>
            {formatFairRoi(summary.fair_roi)}
        </div>
        <div>
            <span className="text-gray-500">Bonus ratio: </span>
            {formatFractionAsPercent(summary.bonus_ratio)}
        </div>
        <div>
            <span className="text-gray-500">Fix: </span>
            {formatPercent(summary.fix_rate)}
        </div>
        <div>
            <span className="text-gray-500">Eff. rate: </span>
            {formatFractionAsPercent(summary.effective_rate)}
            {summary.cap_applied ? ' (ограничено выкупом 60%)' : ''}
        </div>
        <div>
            <span className="text-gray-500">Выкуп: </span>
            {formatYesNo(summary.has_share)}
        </div>
        <div>
            <span className="text-gray-500">Итог бонус: </span>
            <span className="font-medium">
                {formatUsd(summary.bonus_usd)}
                {summary.is_current ? ' (предварительно)' : ''}
            </span>
        </div>
    </div>
);

const BreakdownContent: React.FC<{ breakdown: ContractBonusBreakdown; isCurrent: boolean }> = ({ breakdown, isCurrent }) => {
    const mismatch = bonusAmountsMismatch(breakdown.total, breakdown.paid_bonus_usd);

    return (
        <>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Пакет</TableCell>
                        <TableCell>Дата расчёта</TableCell>
                        <TableCell align="right">Result</TableCell>
                        <TableCell align="right">Списания</TableCell>
                        <TableCell align="right">Share</TableCell>
                        <TableCell align="right">Payout</TableCell>
                        <TableCell align="right">Tot Payout</TableCell>
                        <TableCell align="right">Eff Payout</TableCell>
                        <TableCell align="right">Вклад</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {breakdown.bundles.map(bundle => {
                        const shareFraction = bundle.personal_share / 100;
                        const totPayout = payoutShareFraction(shareFraction, bundle.payout / 100);
                        const effPayout = payoutShareFraction(shareFraction, breakdown.effective_rate);

                        return (
                            <TableRow key={bundle.bundle_id}>
                                <TableCell>#{bundle.bundle_id}</TableCell>
                                <TableCell>
                                    {bundle.end_datetime
                                        ? moment(bundle.end_datetime).format('DD MMM YYYY HH:mm')
                                        : bundle.active
                                        ? 'текущий'
                                        : '—'}
                                </TableCell>
                                <TableCell align="right">{formatUsd(bundle.result_usd, true)}</TableCell>
                                <TableCell align="right">{formatUsd(bundle.profit_deductions_usd)}</TableCell>
                                <TableCell align="right">{formatPercent(bundle.personal_share)}</TableCell>
                                <TableCell align="right">{formatPercent(bundle.payout)}</TableCell>
                                <TableCell align="right">{formatFractionAsPercent(totPayout)}</TableCell>
                                <TableCell align="right">{formatFractionAsPercent(effPayout)}</TableCell>
                                <TableCell
                                    align="right"
                                    className={bundle.contribution < 0 ? 'text-red-600' : undefined}
                                >
                                    {formatUsd(bundle.contribution, true)}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>

            <div className="mt-4 space-y-1 text-sm">
                <p>
                    <span className="text-gray-500">Подытог: </span>
                    <span className={breakdown.subtotal < 0 ? 'text-red-600' : undefined}>{formatUsd(breakdown.subtotal, true)}</span>
                </p>
                <p>
                    <span className="text-gray-500">Итог: </span>
                    <span className="font-medium">
                        {formatUsd(breakdown.total)}
                        {isCurrent ? ' (предварительно)' : ''}
                    </span>
                </p>
                <p>
                    <span className="text-gray-500">Выплачено (в ЛК): </span>
                    {formatUsd(breakdown.paid_bonus_usd)}
                    {!mismatch && breakdown.paid_bonus_usd != null ? ' ✓ совпадает' : null}
                </p>
            </div>

            {mismatch ? (
                <Alert severity="warning" className="mt-4">
                    Итог ({formatUsd(breakdown.total)}) и выплаченная сумма ({formatUsd(breakdown.paid_bonus_usd)}) не совпадают.
                    {isCurrent
                        ? ' На текущем контракте это нормально в моменте.'
                        : ' Возможно, воркер ещё не обновил данные после загрузки турниров.'}
                </Alert>
            ) : null}
        </>
    );
};

const BonusBreakdownDialog: React.FC<BonusBreakdownDialogProps> = ({ contractSummary, ...props }) => {
    const { playerId } = useParams();
    const contractId = contractSummary?.contract_id;

    const breakdownQuery = useQuery<ContractBonusBreakdown>(
        ['contract-bonus-breakdown', playerId, contractId],
        getContractBonusBreakdown,
        {
            enabled: !!playerId && !!contractId && props.open,
        }
    );

    return (
        <Dialog {...props} maxWidth="md" fullWidth>
            <DialogTitle>Детализация бонуса</DialogTitle>
            <DialogContent>
                {contractSummary ? (
                    <>
                        <p className="text-sm text-gray-600 mb-4">{formatContractPeriod(contractSummary)}</p>
                        <SummaryStats summary={contractSummary} />

                        {breakdownQuery.isLoading ? (
                            <div className="flex justify-center py-8">
                                <CircularProgress size={32} />
                            </div>
                        ) : breakdownQuery.isError ? (
                            <Alert severity="error">Не удалось загрузить детализацию</Alert>
                        ) : breakdownQuery.data ? (
                            <BreakdownContent breakdown={breakdownQuery.data} isCurrent={contractSummary.is_current} />
                        ) : null}
                    </>
                ) : null}
            </DialogContent>
        </Dialog>
    );
};

export default BonusBreakdownDialog;
